import React, { useRef, useEffect, useState } from "react";
import {
  TabSectionContainer,
  TabContainer,
  TabButton,
  ContentWrapper,
} from "./styles";
import ContentSection from "../ContentSection";
import FAQSection from "../FAQSection";
import banners from "../../../data/banners.json";
import StatisticsSection from "../StatisticsSection";
import DeviceSection from "../DeviceSection";
import CloudComplianceSection from "../CloudComplianceSection";

interface TabSectionProps {
  selectedTab: string | null;
  onTabChange: (tab: string) => void;
}
const TabSection: React.FC<TabSectionProps> = ({
  selectedTab,
  onTabChange,
}) => {
  const smartRef = useRef<HTMLDivElement>(null);
  const safeRef = useRef<HTMLDivElement>(null);
  const anywhereRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>("smart");
  const isManualScrollRef = useRef<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      if (isManualScrollRef.current) return;
      if (!smartRef.current || !safeRef.current || !anywhereRef.current) return;

      const smartRect = smartRef.current.getBoundingClientRect();
      const safeRect = safeRef.current.getBoundingClientRect();
      const anywhereRect = anywhereRef.current.getBoundingClientRect();

      const viewportHeight = window.innerHeight;

      const getVisibleHeight = (rect: DOMRect) => {
        const top = Math.max(0, rect.top);
        const bottom = Math.min(viewportHeight, rect.bottom);
        return Math.max(0, bottom - top);
      };

      const smartVisible = getVisibleHeight(smartRect);
      const safeVisible = getVisibleHeight(safeRect);
      const anywhereVisible = getVisibleHeight(anywhereRect);

      let newTab = activeTab;
      const maxVisible = Math.max(smartVisible, safeVisible, anywhereVisible);

      if (maxVisible > 0) {
        if (maxVisible === smartVisible) newTab = "smart";
        else if (maxVisible === safeVisible) newTab = "safe";
        else if (maxVisible === anywhereVisible) newTab = "anywhere";
      } else {
        const distances = [
          { id: "smart", dist: Math.abs(smartRect.top) },
          { id: "safe", dist: Math.abs(safeRect.top) },
          { id: "anywhere", dist: Math.abs(anywhereRect.top) },
        ];

        const closest = distances.reduce((prev, curr) =>
          curr.dist < prev.dist ? curr : prev
        );
        newTab = closest.id;
      }

      if (newTab !== activeTab) {
        setActiveTab(newTab);
        onTabChange(newTab);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    // 500ms 간격으로 변경 (성능과 정확도의 밸런스)
    const intervalCheck = setInterval(handleScroll, 500);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearInterval(intervalCheck);
    };
  }, [onTabChange, activeTab]);

  const handleTabClick = (tab: string) => {
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {
      smart: smartRef,
      safe: safeRef,
      anywhere: anywhereRef,
    };

    setActiveTab(tab);
    onTabChange(tab);

    isManualScrollRef.current = true; // 수동 스크롤 시작

    const currentRef = refs[tab];
    if (currentRef.current) {
      currentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // 스크롤 애니메이션이 끝나면 자동 스크롤 감지 다시 활성화
      setTimeout(() => {
        isManualScrollRef.current = false;
      }, 1000); // 스크롤 애니메이션 시간보다 조금 더 길게 설정
    }
  };

  return (
    <TabSectionContainer>
      <TabContainer>
        <TabButton
          $isSelected={activeTab === "smart"}
          onClick={() => handleTabClick("smart")}
        >
          스마트하게
        </TabButton>
        <TabButton
          $isSelected={activeTab === "safe"}
          onClick={() => handleTabClick("safe")}
        >
          안전하게
        </TabButton>
        <TabButton
          $isSelected={activeTab === "anywhere"}
          onClick={() => handleTabClick("anywhere")}
        >
          언제/어디서나
        </TabButton>
      </TabContainer>

      <ContentWrapper>
        <div ref={smartRef}>
          <ContentSection
            mainTitle={"스마일보건의\n키오스크 기능"}
            subTitle="보건실 방문 학생을 더욱 스마트하게 관리하세요"
            textAlign="center"
            media={[
              {
                type: "video",
                src: "/videos/kiosk_meet_teacher.mp4",
                alt: "키오스크 기능 소개",
              },
            ]}
          />
        </div>

        <div ref={safeRef}>
          <CloudComplianceSection />
          <ContentSection
            mainTitle={"2,500개+ 학교에서\n스마일보건을 사용하고 있습니다."}
            banners={banners}
            textAlign="center"
          />
          <StatisticsSection />

          <ContentSection
            mainTitle={"스마일몰에서\n편리하게 구입하세요."}
            subTitle={
              "보건선생님께 필요한 모든 물품이 여기에!\n스마일보건과 함께 쓰는 스마일몰!"
            }
            media={[
              {
                type: "image",
                src: "/images/home-mall.webp",
                alt: "스마일몰",
                maxWidth: 1500,
              },
            ]}
            direction="column"
            textAlign="center"
          />
        </div>

        <div ref={anywhereRef}>
          <DeviceSection />
        </div>

        <FAQSection />
      </ContentWrapper>
    </TabSectionContainer>
  );
};

export default TabSection;
